import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { startOfDay } from 'date-fns';
import { isEqual } from 'lodash';
import { tap } from 'rxjs/operators';

import {
  Form,
  FormData,
  FormsService,
  ProgramPeriod,
  ProgramPeriodData,
  ProgramPeriodRiskConfigData,
} from '@assess/browser/api';
import { createValidatorFromSchema } from '@assess/browser/form-validation';
import { formLevelDetailsSchema, IDateOfServiceMinMax } from '@assess/shared/entities';

@Component({
  selector: 'assess-form-level-details',
  templateUrl: './form-level-details.component.html',
  styleUrls: ['./form-level-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormLevelDetailsComponent implements OnInit {
  @Input() programPeriodData!: ProgramPeriodData;
  @Input() programPeriod!: ProgramPeriod;
  @Input() riskConfig!: ProgramPeriodRiskConfigData;
  @Input() dateOfServiceMinMax!: IDateOfServiceMinMax;
  @Input() form!: Form;
  @Input() review = false;

  @Output() formDataUpdate = new EventEmitter<FormData>();
  @Output() isValid = new EventEmitter<boolean>();

  enabled = true;
  formGroup!: FormGroup;
  initialValue: FormData = { dateOfService: '', provider: {} };
  expanded = false;
  saveDisabled = false;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    private readonly formsService: FormsService
  ) {}

  ngOnInit(): void {
    this.enabled = !!(
      this.programPeriodData?.settings.riskEnabled &&
      (this.riskConfig?.formProviderEnabled || this.riskConfig?.formDosEnabled)
    );
    if (!this.enabled) {
      return;
    }

    this.formGroup = this.formBuilder.group({});

    this.addValidators();

    if (this.riskConfig.formProviderEnabled) {
      this.setUpProvider();
    }

    if (this.riskConfig.formDosEnabled) {
      this.setUpDateOfService();
    }

    if (!this.formGroup.valid) {
      this.checkAutofill();
      this.expanded = true;
      this.saveDisabled = true;
    }

    this.isValid.emit(this.formGroup.valid);

    this.formGroup.valueChanges
      .pipe(
        tap((changes) => {
          if (isEqual(changes, this.initialValue)) {
            this.saveDisabled = true;

            return;
          }

          this.saveDisabled = !this.formGroup.valid;
        })
      )
      .subscribe();
  }

  get provider() {
    const name = this.formGroup.get('provider')?.value?.Name;
    const npi = this.formGroup.get('provider')?.value?.NPI;

    if (!name || !npi) {
      return '';
    }

    return `${name} (${npi})`;
  }

  addValidators() {
    this.formGroup.addValidators(
      createValidatorFromSchema(formLevelDetailsSchema, {
        dateOfServiceMax: this.dateOfServiceMinMax?.max,
        dateOfServiceMin: this.dateOfServiceMinMax?.min,
        formProviderEnabled: this.riskConfig.formProviderEnabled,
        formDosEnabled: this.riskConfig.formDosEnabled,
      })
    );
  }

  setUpDateOfService() {
    this.initialValue.dateOfService = this.form.data?.dateOfService;

    this.formGroup.addControl('dateOfService', new FormControl(this.form.data?.dateOfService));
  }

  setUpProvider() {
    this.initialValue.provider = this.form.data?.provider;

    this.formGroup.addControl('provider', new FormControl(this.form.data?.provider));
  }

  checkAutofill() {
    if (
      this.riskConfig.autofillDosEnabled &&
      this.riskConfig.formDosEnabled &&
      !this.formGroup.get('dateOfService')?.value
    ) {
      this.formGroup.get('dateOfService')?.setValue(startOfDay(new Date()));
    }
  }

  edit() {
    this.expanded = true;
    this.saveDisabled = true;
  }

  cancel() {
    this.formGroup.reset(this.initialValue);
    this.expanded = false;
  }

  save() {
    this.saveDisabled = true;
    this.formsService
      .updateData({
        formId: this.form.id,
        body: this.formGroup.value,
      })
      .subscribe(() => {
        this.initialValue = this.formGroup.value;
        this.expanded = false;
        this.form = { ...this.form, data: this.formGroup.value };
        this.formDataUpdate.emit(this.form.data);
        this.isValid.emit(this.formGroup.valid);
        this.changeDetectorRef.detectChanges();
      });
  }
}
